// TradeJournalPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow,
  IconButton, Chip, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  Divider, TableSortLabel, Grid, Card, CardContent
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import ImageIcon from '@mui/icons-material/Image';
import TradeFilters from '../components/common/TradeFilters';
import useTradeFilters from '../hooks/useTradeFilters';
import TradeFormFields from '../components/trades/TradeFormFields';
import { calculateTradeMetrics } from '../utils/tradeUtils';
import alertService from '../services/alertService';
import axios from 'axios';

const TradeJournalPage = () => {
  const [trades, setTrades] = useState([]);
  const [strategies, setStrategies] = useState([]);
  const [strategyNamesMap, setStrategyNamesMap] = useState({});
  const [editTradeOpen, setEditTradeOpen] = useState(false);
  const [editingTrade, setEditingTrade] = useState(null);
  const [previewImage, setPreviewImage] = useState('');
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('tradeDate');
  
  const {
    selectedCategory, selectedSymbol, selectedStrategy, selectedStatus, selectedTags, startDate, endDate,
    setSelectedCategory, setSelectedSymbol, setSelectedStrategy, setSelectedStatus, setSelectedTags, setStartDate, setEndDate,
    filteredTrades, symbols, statuses, allTags, handleTimeFilter
  } = useTradeFilters(trades);

  useEffect(() => {
    fetchTrades();
    fetchStrategies();
  }, []);

  const fetchTrades = async () => {
    try {
      const token = localStorage.getItem('idToken');
      const response = await axios.get('https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/getTrades', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrades(response.data);
      // Update alerts when trades are fetched
      alertService.updateAlerts(response.data);
    } catch (error) {
      console.error('Error fetching trades:', error);
    }
  };

  const fetchStrategies = async () => {
    try {
      const token = localStorage.getItem('idToken');
      const response = await axios.get('https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/createTradeStrategy', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const strategyMap = {};
      for (const strat of response.data) {
        strategyMap[strat.strategyId] = strat.name;
      }
      setStrategies(response.data);
      setStrategyNamesMap(strategyMap);
    } catch (error) {
      console.error('Error fetching strategies:', error);
    }
  };

  const handleEditTrade = useCallback((trade) => {
    setEditingTrade({
      ...trade,
      category: trade.category || 'stock',
      leverage: Number(trade.leverage) || 1,
      positionSize: Number(trade.positionSize) || '',
      strategyIds: trade.strategyIds || [],
      tags: trade.tags || '',
      rating: trade.rating || 3,
      exchange: trade.exchange || ''
    });
    setEditTradeOpen(true);
  }, []);
  
  const handleTradeChange = useCallback((field, value) => {
    setEditingTrade(prev => {
      const updated = { ...prev, [field]: value };
      
      // Only auto-calculate P&L if manual override is not enabled
      if (!updated.manualPnL && field === 'status') {
        const reward = parseFloat(updated.rewardAmount);
        const risk = parseFloat(updated.riskAmount);
        const status = value;
        
        if (!isNaN(reward) && !isNaN(risk)) {
          const pnl =
            status === 'WIN' ? reward.toFixed(2) :
            status === 'LOSS' ? `-${risk.toFixed(2)}` :
            status === 'BREAKEVEN' ? '0' : '';
          updated.profitLoss = pnl;
          updated.pnl = pnl;
        }
      }
      
      return updated;
    });
  }, []);
  
  const handleSaveTrade = async () => {
    try {
      const token = localStorage.getItem('idToken');
      
      // Calculate metrics using shared utility function
      const metrics = calculateTradeMetrics(
        editingTrade.entryPrice,
        editingTrade.stopLoss,
        editingTrade.exitPrice,
        editingTrade.positionSize,
        editingTrade.leverage
      );
      
      // Calculate profit/loss based on status
      if (!editingTrade.manualPnL) {
        const reward = parseFloat(metrics.rewardAmount);
        const risk = parseFloat(metrics.riskAmount);
        const status = editingTrade.status;

        if (!isNaN(reward) && !isNaN(risk)) {
          editingTrade.profitLoss =
            status === 'WIN' ? reward.toFixed(2) :
            status === 'LOSS' ? `-${risk.toFixed(2)}` :
            status === 'BREAKEVEN' ? '0' :
            '';
          editingTrade.pnl = editingTrade.profitLoss;
        }
      }
      
      const payload = {
        ...editingTrade,
        ...metrics,
        rrSummary: metrics.riskRewardRatio ? `R:R = ${metrics.riskRewardRatio} | Risk: $${metrics.riskAmount} | Reward: $${metrics.rewardAmount} | Quantity: ${metrics.quantity}` : ''
      };
      
      await axios.post('https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/trades', payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setEditTradeOpen(false);
      setEditingTrade(null);
      await fetchTrades();
      // Update alerts after editing trade
      alertService.updateAlerts(trades.map(t => t.tradeId === editingTrade.tradeId ? payload : t));
    } catch (err) {
      console.error('Error updating trade:', err);
    }
  };
  
  const handleDeleteTrade = useCallback(async (tradeId) => {
    try {
      const token = localStorage.getItem('idToken');
      await axios.post('https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/deleteTrade', {
        tradeId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setEditTradeOpen(false);
      setEditingTrade(null);
      await fetchTrades();
    } catch (error) {
      console.error('Error deleting trade', error);
    }
  }, [fetchTrades]);
  
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };
  
  const sortedTrades = React.useMemo(() => {
    const comparator = (a, b) => {
      // Handle numeric values
      if (['entryPrice', 'exitPrice', 'stopLoss', 'profitLoss', 'riskRewardRatio'].includes(orderBy)) {
        const aValue = parseFloat(a[orderBy]) || 0;
        const bValue = parseFloat(b[orderBy]) || 0;
        return order === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      // Handle dates
      if (orderBy === 'tradeDate') {
        return order === 'asc' 
          ? new Date(a[orderBy]) - new Date(b[orderBy])
          : new Date(b[orderBy]) - new Date(a[orderBy]);
      }
      
      // Handle strings and other values
      if (a[orderBy] < b[orderBy]) {
        return order === 'asc' ? -1 : 1;
      }
      if (a[orderBy] > b[orderBy]) {
        return order === 'asc' ? 1 : -1;
      }
      return 0;
    };
    
    return [...filteredTrades].sort(comparator);
  }, [filteredTrades, order, orderBy]);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
        📊 All Trades
      </Typography>
      
      {/* Filter Section */}
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 3 }}>
        <TradeFilters
          selectedCategory={selectedCategory}
          selectedSymbol={selectedSymbol}
          selectedStrategy={selectedStrategy}
          selectedStatus={selectedStatus}
          selectedTags={selectedTags}
          startDate={startDate}
          endDate={endDate}
          onCategoryChange={setSelectedCategory}
          onSymbolChange={setSelectedSymbol}
          onStrategyChange={setSelectedStrategy}
          onStatusChange={setSelectedStatus}
          onTagsChange={setSelectedTags}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          onTimeFilter={handleTimeFilter}
          symbols={symbols}
          strategies={strategies.map(s => s.strategyId)}
          statuses={statuses}
          allTags={allTags}
          strategyNamesMap={strategyNamesMap}
        />
      </Paper>
      
      {/* Stats Cards */}
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography variant="h6">Total Trades</Typography>
              <Typography variant="h3" color="primary">{filteredTrades.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography variant="h6">Win Rate</Typography>
              <Typography variant="h5">
                {(() => {
                  const wins = filteredTrades.filter(t => t.status === 'WIN').length;
                  const total = filteredTrades.filter(t => ['WIN', 'LOSS', 'BREAKEVEN'].includes(t.status)).length;
                  return total > 0 ? `${((wins / total) * 100).toFixed(1)}%` : 'N/A';
                })()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography variant="h6">Total P&L</Typography>
              <Typography variant="h5" sx={{ 
                color: (() => {
                  const totalPnL = filteredTrades.reduce((sum, trade) => sum + parseFloat(trade.profitLoss || 0), 0);
                  return totalPnL >= 0 ? '#4caf50' : '#f44336';
                })()
              }}>
                ${filteredTrades.reduce((sum, trade) => sum + parseFloat(trade.profitLoss || 0), 0).toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Trade List */}
      <Paper sx={{ p: { xs: 1, sm: 2 }, overflow: 'auto' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>Trade History</Typography>
          <Button 
            variant="contained" 
            color="primary"
            onClick={() => window.location.href = '/ai-trade-create'}
          >
            Create Trade with AI
          </Button>
        </Box>
        
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'symbol'}
                  direction={orderBy === 'symbol' ? order : 'asc'}
                  onClick={() => handleRequestSort('symbol')}
                >
                  Symbol
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'tradeDate'}
                  direction={orderBy === 'tradeDate' ? order : 'asc'}
                  onClick={() => handleRequestSort('tradeDate')}
                >
                  Date
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'status'}
                  direction={orderBy === 'status' ? order : 'asc'}
                  onClick={() => handleRequestSort('status')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'entryPrice'}
                  direction={orderBy === 'entryPrice' ? order : 'asc'}
                  onClick={() => handleRequestSort('entryPrice')}
                >
                  Entry
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'exitPrice'}
                  direction={orderBy === 'exitPrice' ? order : 'asc'}
                  onClick={() => handleRequestSort('exitPrice')}
                >
                  Exit
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'stopLoss'}
                  direction={orderBy === 'stopLoss' ? order : 'asc'}
                  onClick={() => handleRequestSort('stopLoss')}
                >
                  Stop Loss
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'profitLoss'}
                  direction={orderBy === 'profitLoss' ? order : 'asc'}
                  onClick={() => handleRequestSort('profitLoss')}
                >
                  P&L
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'riskRewardRatio'}
                  direction={orderBy === 'riskRewardRatio' ? order : 'asc'}
                  onClick={() => handleRequestSort('riskRewardRatio')}
                >
                  R:R
                </TableSortLabel>
              </TableCell>
              <TableCell>Strategy</TableCell>
              <TableCell>Image</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedTrades.map((trade) => (
              <React.Fragment key={trade.tradeId}>
                <TableRow hover>
                  <TableCell>{trade.symbol}</TableCell>
                  <TableCell>{trade.tradeDate}</TableCell>
                  <TableCell>
                    <Chip 
                      label={trade.status} 
                      size="small" 
                      color={
                        trade.status === 'WIN' ? 'success' : 
                        trade.status === 'LOSS' ? 'error' : 
                        trade.status === 'BREAKEVEN' ? 'warning' : 
                        'default'
                      }
                    />
                  </TableCell>
                  <TableCell>${trade.entryPrice}</TableCell>
                  <TableCell>${trade.exitPrice || 'N/A'}</TableCell>
                  <TableCell>${trade.stopLoss || 'N/A'}</TableCell>
                  <TableCell sx={{ color: parseFloat(trade.profitLoss || 0) >= 0 ? 'green' : 'red', fontWeight: 'bold' }}>
                    ${trade.profitLoss || '0'}
                  </TableCell>
                  <TableCell>{trade.riskRewardRatio || 'N/A'}</TableCell>
                  <TableCell>
                    {(trade.strategyIds || []).map((id) => (
                      <Chip 
                        key={id} 
                        label={strategyNamesMap[id] || id} 
                        size="small" 
                        sx={{ mr: 0.5, mb: 0.5 }} 
                      />
                    ))}
                  </TableCell>
                  <TableCell>
                    {(trade.imageUrl || trade.screenshot) ? (
                      <IconButton 
                        size="small" 
                        onClick={() => setPreviewImage(trade.imageUrl || `data:image/png;base64,${trade.screenshot}`)}
                        sx={{ 
                          bgcolor: 'primary.main',
                          color: 'primary.contrastText',
                          '&:hover': { bgcolor: 'primary.dark' }
                        }}
                      >
                        <ImageIcon fontSize="small" />
                      </IconButton>
                    ) : (
                      <Typography variant="caption" color="text.disabled">—</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <IconButton 
                      size="small" 
                      color="primary"
                      onClick={() => handleEditTrade(trade)}
                      title="Edit trade"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      color="error"
                      onClick={() => handleDeleteTrade(trade.tradeId)}
                      title="Delete trade"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
                

              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </Paper>
      
      {/* Image Preview Dialog */}
      <Dialog 
        open={!!previewImage} 
        onClose={() => setPreviewImage('')} 
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          bgcolor: 'primary.main',
          color: 'primary.contrastText'
        }}>
          <Typography variant="h6">📊 Trade Chart</Typography>
          <IconButton 
            onClick={() => setPreviewImage('')}
            sx={{ color: 'primary.contrastText' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            minHeight: 400
          }}>
            <img 
              src={previewImage} 
              alt="Trade Screenshot" 
              style={{ 
                maxWidth: '100%', 
                maxHeight: '80vh',
                borderRadius: 8
              }} 
            />
          </Box>
        </DialogContent>
      </Dialog>
      
      {/* Edit Trade Dialog */}
      <Dialog open={editTradeOpen} onClose={() => setEditTradeOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          Edit Trade - {editingTrade?.symbol}
        </DialogTitle>
        <DialogContent sx={{ pt: { xs: 3, sm: 6 }, pb: 3, px: { xs: 2, sm: 3 } }}>
          {editingTrade && (
            <Box sx={{ mt: 2 }}>
              <TradeFormFields
                values={editingTrade}
                onChange={handleTradeChange}
                strategies={strategies}
                trades={trades}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => setEditTradeOpen(false)}
            sx={{ minWidth: 100 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveTrade}
            sx={{ minWidth: 100 }}
          >
            Save
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={() => handleDeleteTrade(editingTrade?.tradeId)}
            startIcon={<DeleteIcon />}
            sx={{ minWidth: 100 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TradeJournalPage;